import { DocumentNode, gql } from '@apollo/client';
import { HStack, Image, Text, VStack } from '@chakra-ui/react';
import * as React from 'react';

import { IRequestDemoView } from '@/types/analytics';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { RequestDemoButton } from '@/imports/ui/components/RequestDemoButton';

import { ConferenceSessionMobileSponsors_SessionFragment } from './graphql-types/ConferenceSessionMobileSponsors_SessionFragment';

export const ConferenceSessionMobileSponsors: React.FC<{
  conferenceSessionData?: ConferenceSessionMobileSponsors_SessionFragment;
  origin: {
    conferenceBoothId?: string | undefined;
    sponsoredEventId?: string | undefined;
    sponsoredConferenceSessionId?: string | undefined;
    hostedEventId?: string | undefined;
    sponsorPage?: boolean | undefined;
  };
  analytics: {
    view: IRequestDemoView;
    tier: string;
  };
}> & { sessionFragment: DocumentNode } = ({
  conferenceSessionData,
  origin,
  analytics,
}) => {
  const keySponsor = conferenceSessionData?.sponsors?.keySponsor;
  const keySponsorImageProxy =
    keySponsor?.sponsorship?.ad?.adImage?.mobileImageProxy;
  const additionalSponsors =
    conferenceSessionData?.sponsors?.additionalSponsors || [];
  const organizationId = keySponsor?._id;
  const registerInterestTitle =
    keySponsor?.sponsorship?.ad?.registerInterestTitle;

  if (!conferenceSessionData) {
    return null;
  }

  return (
    <VStack spacing="2">
      {keySponsor && (
        <VStack
          w="full"
          backgroundColor="white"
          p="4"
          boxShadow="md"
          spacing="4"
        >
          {keySponsorImageProxy ? (
            <Image
              src={keySponsorImageProxy}
              w="100px"
              borderRadius="lg"
              alt={`Sponsor promotion for ${keySponsor.name}`}
            />
          ) : (
            <ImageFallback borderRadius="lg" />
          )}
          <Text fontWeight="semibold" fontSize="xl">
            {keySponsor.name}
          </Text>
          <Text>{keySponsor.sponsorship?.ad?.body}</Text>
          <RequestDemoButton
            options={{
              organizationId: organizationId || '',
              origin,
              registerInterestTitle: registerInterestTitle || 'Request Demo',
              analytics,
            }}
            w="full"
          />
        </VStack>
      )}

      {additionalSponsors.length > 0 && (
        <VStack
          p="4"
          backgroundColor="white"
          shadow={1}
          w="100%"
          boxShadow="md"
          alignItems="flex-start"
          spacing="2"
        >
          <Text fontWeight="semibold">Additional Sponsors</Text>
          {additionalSponsors.map((sponsor) => {
            const additionalSponsorImageProxy =
              sponsor.profilePicture?.imageProxy;
            return (
              <HStack key={sponsor._id}>
                {additionalSponsorImageProxy ? (
                  <Image
                    src={additionalSponsorImageProxy}
                    borderRadius="lg"
                    boxSize="16"
                    alt={`additional sponsor ${sponsor.name}`}
                  />
                ) : (
                  <ImageFallback boxSize="16" borderRadius="lg" />
                )}
                <Text noOfLines={2}>{sponsor.name}</Text>
              </HStack>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

ConferenceSessionMobileSponsors.sessionFragment = gql`
  fragment ConferenceSessionMobileSponsors_SessionFragment on ConferenceSession {
    _id
    sponsors {
      keySponsor {
        ... on Organization {
          _id
          name
          sponsorship {
            ad {
              _id
              body
              registerInterestTitle
              adImage {
                mobileImageProxy: imageProxy(
                  input: { h: 144, w: 146, fit: "crop" }
                )
              }
            }
          }
        }
      }
      additionalSponsors {
        ... on Organization {
          _id
          name
          profilePicture {
            _id
            imageProxy(input: { h: 144, w: 146, fit: "crop" })
          }
        }
      }
    }
  }
`;
